import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bell, Settings, Send, Loader2, RefreshCcw, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { fmtInr, fmtPct } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

function ThresholdChip({ pct }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-loss/40 bg-loss/10 text-loss text-[10px] uppercase tracking-[0.2em] font-mono font-semibold px-2 py-1 rounded">
      <AlertTriangle className="h-3 w-3" strokeWidth={2} />
      Below {Number(pct).toFixed(1)}%
    </span>
  );
}

function AlertRow({ a }) {
  return (
    <Link
      to={`/advisor/clients/${a.client_id}`}
      data-testid={`alert-row-${a.symbol}-${a.client_id}`}
      className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/60 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold text-foreground">{a.symbol}</span>
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground">
            {a.client_name || a.client_code}
          </span>
        </div>
        {a.name ? (
          <div className="text-[11px] text-muted-foreground truncate mt-0.5">{a.name}</div>
        ) : null}
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono tabular text-sm font-bold text-loss">{fmtPct(a.change_pct)}</div>
        <div className="font-mono tabular text-[10px] text-muted-foreground mt-0.5">
          {fmtInr(a.unrealized_pnl)}
        </div>
      </div>
    </Link>
  );
}

function SettingsDialog({ open, onOpenChange, settings, onSaved }) {
  const [form, setForm] = useState({
    alert_email: "",
    threshold_pct: -20,
    email_enabled: true,
    daily_schedule_ist: "09:30",
    weekly_enabled: true,
    weekly_day: "MON",
    weekly_time_ist: "08:00",
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);

  useEffect(() => {
    if (open && settings) {
      setForm({
        alert_email: settings.alert_email || "",
        threshold_pct: Number(settings.threshold_pct ?? -20),
        email_enabled: settings.email_enabled !== false,
        daily_schedule_ist: settings.daily_schedule_ist || "09:30",
        weekly_enabled: settings.weekly_enabled !== false,
        weekly_day: settings.weekly_day || "MON",
        weekly_time_ist: settings.weekly_time_ist || "08:00",
      });
    }
  }, [open, settings]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        threshold_pct: Number(form.threshold_pct),
      };
      const { data } = await apiClient.patch("/advisor/settings", payload);
      toast.success("Alert settings updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await apiClient.post("/advisor/alerts/send-now");
      if (data.email_sent) {
        toast.success(`Digest emailed to ${data.recipient}`);
      } else {
        toast.info(data.skipped_reason ? `Skipped: ${data.skipped_reason}` : "Digest evaluated");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const sendWeeklyTest = async () => {
    setSendingWeekly(true);
    try {
      const { data } = await apiClient.post("/advisor/weekly/send-now");
      if (data.email_sent) {
        toast.success(`Weekly summary emailed to ${data.recipient}`);
      } else {
        toast.info(data.skipped_reason ? `Skipped: ${data.skipped_reason}` : "Weekly summary evaluated");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSendingWeekly(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Alert settings</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              Alert email
            </label>
            <input
              data-testid="alert-email-input"
              value={form.alert_email}
              onChange={(e) => setForm((s) => ({ ...s, alert_email: e.target.value }))}
              placeholder="you@firm.com"
              className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring rounded-md"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              Threshold (overall return %)
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input
                data-testid="alert-threshold-input"
                type="number"
                step="0.5"
                value={form.threshold_pct}
                onChange={(e) => setForm((s) => ({ ...s, threshold_pct: e.target.value }))}
                className="w-32 border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring rounded-md tabular font-mono"
              />
              <span className="text-xs text-muted-foreground">
                Alert when any holding&apos;s overall return drops to this or lower (e.g. -20)
              </span>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              Daily digest time (IST)
            </label>
            <input
              data-testid="alert-schedule-input"
              type="time"
              value={form.daily_schedule_ist}
              onChange={(e) => setForm((s) => ({ ...s, daily_schedule_ist: e.target.value }))}
              className="mt-1 w-32 border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring rounded-md tabular font-mono"
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Restart the backend for a new schedule time to take effect.
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              data-testid="alert-email-enabled"
              type="checkbox"
              checked={form.email_enabled}
              onChange={(e) => setForm((s) => ({ ...s, email_enabled: e.target.checked }))}
              className="accent-[hsl(var(--primary))] h-4 w-4"
            />
            Send email digest daily
          </label>

          <div className="border-t border-border pt-4 mt-1">
            <div className="text-[10px] uppercase tracking-[0.22em] font-mono font-semibold text-muted-foreground mb-3">
              Weekly performance summary
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
              <input
                data-testid="weekly-enabled"
                type="checkbox"
                checked={form.weekly_enabled}
                onChange={(e) => setForm((s) => ({ ...s, weekly_enabled: e.target.checked }))}
                className="accent-[hsl(var(--primary))] h-4 w-4"
              />
              Send weekly book review email
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  Day
                </label>
                <select
                  data-testid="weekly-day"
                  value={form.weekly_day}
                  onChange={(e) => setForm((s) => ({ ...s, weekly_day: e.target.value }))}
                  className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring rounded-md font-mono"
                >
                  {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  Time (IST)
                </label>
                <input
                  data-testid="weekly-time-input"
                  type="time"
                  value={form.weekly_time_ist}
                  onChange={(e) => setForm((s) => ({ ...s, weekly_time_ist: e.target.value }))}
                  className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring rounded-md tabular font-mono"
                />
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              Covers top gainers/losers, AI red flags, and per-client returns across the book.
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <button
            data-testid="alert-send-test"
            onClick={sendTest}
            disabled={sending || !form.alert_email}
            className="border border-border hover:bg-secondary px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-50 inline-flex items-center gap-1.5 rounded-md"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send daily test
          </button>
          <button
            data-testid="weekly-send-test"
            onClick={sendWeeklyTest}
            disabled={sendingWeekly || !form.alert_email}
            className="border border-border hover:bg-secondary px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-50 inline-flex items-center gap-1.5 rounded-md"
          >
            {sendingWeekly ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MailCheck className="h-3.5 w-3.5" />}
            Send weekly test
          </button>
          <button
            data-testid="alert-settings-save"
            onClick={save}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:brightness-110 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AlertsFeed() {
  const [alerts, setAlerts] = useState({ items: [], count: 0, threshold_pct: -20 });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [a, s] = await Promise.all([
        apiClient.get("/advisor/alerts"),
        apiClient.get("/advisor/settings"),
      ]);
      setAlerts(a.data);
      setSettings(s.data);
    } catch (err) {
      // Fail silently on dashboard load — panel just shows empty state
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="card-noir rounded-lg overflow-hidden" data-testid="alerts-feed">
      <header className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-border bg-secondary/40">
        <div className="flex items-center gap-2.5 min-w-0">
          <Bell className="h-4 w-4 text-primary" strokeWidth={2} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-mono font-semibold text-muted-foreground">
              Risk Alerts
            </div>
            <div className="text-sm font-semibold text-foreground mt-0.5 truncate">
              {loading ? "Scanning…" : (
                alerts.count === 0
                  ? "All holdings within threshold"
                  : `${alerts.count} holding${alerts.count === 1 ? "" : "s"} breached`
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {settings ? <ThresholdChip pct={settings.threshold_pct} /> : null}
          <button
            data-testid="alerts-refresh"
            onClick={load}
            disabled={refreshing}
            title="Refresh"
            className="border border-border hover:bg-card px-2 py-1.5 rounded-md disabled:opacity-50"
          >
            <RefreshCcw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={2} />
          </button>
          <button
            data-testid="alerts-settings-open"
            onClick={() => setOpen(true)}
            title="Alert settings"
            className="border border-border hover:bg-card px-2 py-1.5 rounded-md inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono"
          >
            <Settings className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="hidden sm:inline">Settings</span>
          </button>
        </div>
      </header>

      <div className="max-h-[320px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : alerts.count === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground inline-flex items-center gap-2 w-full justify-center">
            <MailCheck className="h-4 w-4 text-profit" strokeWidth={2} />
            No breaches. Daily digest at {settings?.daily_schedule_ist || "09:30"} IST · sent to {settings?.alert_email || "—"}.
          </div>
        ) : (
          alerts.items.map((a) => (
            <AlertRow key={`${a.client_id}-${a.symbol}`} a={a} />
          ))
        )}
      </div>

      <SettingsDialog
        open={open}
        onOpenChange={setOpen}
        settings={settings}
        onSaved={(s) => { setSettings(s); load(); }}
      />
    </section>
  );
}
